import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideAngularModule, Pencil, Plus, ReceiptIndianRupee, Trash2 } from 'lucide-angular';
import { Charge } from '../core/models/domain.models';
import { ToastService } from '../shared/ui/toast.service';
import { ChargesService, ChargePayload } from '../features/charges/services/charges.service';

@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  styleUrl: './charges.page.scss',
  template: `
    <div class="vendor-page">
      <div class="vendor-shell p-3 p-lg-4">
        <div class="d-flex flex-column flex-xl-row justify-content-between gap-3 align-items-xl-center">
          <div class="d-flex align-items-center gap-3">
            <span class="title-badge">
              <lucide-icon [img]="ReceiptIcon" [size]="20"></lucide-icon>
            </span>
            <div>
              <div class="vendor-page-header__eyebrow">Master Setting</div>
              <h1 class="page-title-lite m-0">Manage Charges</h1>
              <p class="vendor-page-subtitle mb-0">Maintain charge masters with effective dates, refundability, and active history in one place.</p>
            </div>
          </div>

          <button class="btn btn-sm create-btn primary d-inline-flex align-items-center gap-2" type="button" (click)="startCreate()">
            <lucide-icon [img]="PlusIcon" [size]="14"></lucide-icon>
            <span>{{ editingId() ? 'ADD ANOTHER CHARGE' : 'ADD CHARGE' }}</span>
          </button>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-12 col-xl-4">
          <div class="section-card h-100">
            <div class="section-header d-flex align-items-center gap-2">
              <lucide-icon [img]="ReceiptIcon" [size]="16"></lucide-icon>
              <span>{{ editingId() ? 'Edit Charge' : 'Create Charge' }}</span>
            </div>
            <div class="section-body">
              <p class="section-copy">Use effective dates to maintain charge revisions over time without losing historical context.</p>

              <form [formGroup]="form" (ngSubmit)="submit()" class="d-grid gap-3">
                <div>
                  <label class="form-label">Charge Group</label>
                  <select class="form-select" formControlName="charge_type">
                    <option value="">Select group</option>
                    @for (group of chargeGroups; track group) {
                      <option [value]="group">{{ group }}</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="form-label">Charge Name</label>
                  <input class="form-control" type="text" formControlName="charge_name" placeholder="Manipur ISTS" />
                </div>
                <div>
                  <label class="form-label">Rate Type</label>
                  <select class="form-select" formControlName="rate_type">
                    <option value="">Select rate type</option>
                    @for (option of rateTypeOptions; track option) {
                      <option [value]="option">{{ option }}</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="form-label">Value (Rs.)</label>
                  <input class="form-control" type="number" min="0" step="0.01" formControlName="value" placeholder="0.00" />
                </div>

                <div>
                  <label class="form-label">Effective Date</label>
                  <input class="form-control" type="date" formControlName="effective_date" />
                </div>

                <div class="toggle-card">
                  <input class="form-check-input" id="charge-non-refundable" type="checkbox" formControlName="is_non_refundable" />
                  <label class="form-check-label ms-2" for="charge-non-refundable">Is non refundable</label>
                </div>

                <div class="toggle-card">
                  <input class="form-check-input" id="charge-active" type="checkbox" formControlName="active" />
                  <label class="form-check-label ms-2" for="charge-active">Active</label>
                </div>

                @if (formError()) {
                  <div class="alert alert-danger py-2 mb-0">{{ formError() }}</div>
                }

                <div class="d-flex gap-2">
                  <button class="btn btn-sm create-btn primary flex-grow-1" type="submit" [disabled]="saving()">
                    {{ saving() ? 'SAVING...' : editingId() ? 'UPDATE CHARGE' : 'SAVE CHARGE' }}
                  </button>
                  @if (editingId()) {
                    <button class="btn btn-sm create-btn export" type="button" (click)="cancelEdit()">CANCEL</button>
                  }
                </div>
              </form>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-8">
          <div class="panel-card p-3 p-lg-4">
            @if (loading()) {
              <div class="empty-state text-center py-5">
                <lucide-icon [img]="ReceiptIcon" [size]="34" class="empty-state__icon"></lucide-icon>
                <div class="mt-2 fw-semibold">Loading charges...</div>
              </div>
            } @else if (!charges().length) {
              <div class="empty-state text-center py-5 px-4">
                <lucide-icon [img]="ReceiptIcon" [size]="34" class="empty-state__icon"></lucide-icon>
                <div class="mt-2 fw-semibold">No charges saved yet.</div>
                <div class="text-secondary small">Create the first charge entry with its effective date.</div>
              </div>
            } @else {
              <div class="d-grid gap-4">
                @for (group of groupedCharges(); track group.title; let groupIndex = $index) {
                  <div class="group-card">
                    <div class="group-card__header">
                      <div class="group-card__title">{{ groupIndex + 1 }}) {{ group.title }}</div>
                      <span class="metric-pill">{{ group.items.length }} records</span>
                    </div>

                    <div class="table-wrap">
                      <div class="table-responsive">
                        <table class="table table-sm align-middle mb-0">
                          <thead class="table-head">
                            <tr>
                              <th>S.No.</th>
                              <th>Trans./Systems</th>
                              <th>Rate Type</th>
                              <th>Rates / Value (Rs.)</th>
                              <th>Effective Date</th>
                              <th>Refundable</th>
                              <th>Status</th>
                              <th class="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (charge of group.items; track charge.id; let i = $index) {
                              <tr>
                                <td>{{ i + 1 }}</td>
                                <td class="fw-semibold">{{ charge.charge_name }}</td>
                                <td><span class="metric-pill metric-pill--muted">{{ charge.rate_type || '-' }}</span></td>
                                <td>{{ formatCurrency(charge.value) }}</td>
                                <td>{{ formatDate(charge.effective_date) }}</td>
                                <td>
                                  <span class="metric-pill" [class.metric-pill--warning]="charge.is_non_refundable" [class.metric-pill--success]="!charge.is_non_refundable">
                                    {{ charge.is_non_refundable ? 'Non-refundable' : 'Refundable' }}
                                  </span>
                                </td>
                                <td>
                                  <span class="status-pill" [class.approved]="charge.active" [class.rejected]="!charge.active">
                                    {{ charge.active ? 'Active' : 'Inactive' }}
                                  </span>
                                </td>
                                <td class="text-end">
                                  <div class="d-inline-flex gap-2">
                                    <button class="action-btn edit" type="button" (click)="startEdit(charge)">
                                      <lucide-icon [img]="PencilIcon" [size]="14"></lucide-icon>
                                    </button>
                                    <button class="action-btn delete" type="button" (click)="remove(charge)">
                                      <lucide-icon [img]="Trash2Icon" [size]="14"></lucide-icon>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  `,
})
export class ChargesPage {
  private readonly fb = inject(FormBuilder);
  private readonly chargesService = inject(ChargesService);
  private readonly toast = inject(ToastService);

  protected readonly PlusIcon = Plus;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly ReceiptIcon = ReceiptIndianRupee;

  protected readonly charges = signal<Charge[]>([]);
  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly formError = signal('');
  protected readonly chargeGroups = ['Transmission Charges', 'Operating Charges', 'Non-Refundable Application Fee'];
  protected readonly rateTypeOptions = ['Rate (₹/MWh)', 'Rate (₹/Day)'];
  protected readonly groupedCharges = computed(() =>
    this.chargeGroups
      .map((group) => ({
        title: group,
        items: [...this.charges()]
          .filter((item) => this.normalizeChargeType(item.charge_type) === this.normalizeChargeType(group))
          .sort((left, right) => {
            const dateCompare = (right.effective_date || '').localeCompare(left.effective_date || '');
            if (dateCompare !== 0) return dateCompare;
            return left.charge_name.localeCompare(right.charge_name);
          }),
      }))
      .filter((group) => group.items.length > 0),
  );

  protected readonly form = this.fb.nonNullable.group({
    charge_type: ['', [Validators.required]],
    charge_name: ['', [Validators.required]],
    rate_type: [''],
    value: [0, [Validators.required, Validators.min(0)]],
    effective_date: ['', [Validators.required]],
    is_non_refundable: [false],
    active: [true],
  });

  constructor() {
    this.resetForm();
    this.load();
  }

  protected startCreate(): void {
    this.editingId.set(null);
    this.formError.set('');
    this.resetForm();
  }

  protected startEdit(charge: Charge): void {
    this.editingId.set(charge.id);
    this.formError.set('');
    this.form.setValue({
      charge_type: charge.charge_type || '',
      charge_name: charge.charge_name || '',
      rate_type: charge.rate_type || '',
      value: Number(charge.value) || 0,
      effective_date: this.toInputDate(charge.effective_date),
      is_non_refundable: !!charge.is_non_refundable,
      active: !!charge.active,
    });
  }

  protected cancelEdit(): void {
    this.startCreate();
  }

  protected submit(): void {
    this.formError.set('');
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.formError.set('Please fill all required fields before saving.');
      return;
    }

    const raw = this.form.getRawValue();
    const payload: ChargePayload = {
      charge_type: raw.charge_type.trim(),
      charge_name: raw.charge_name.trim(),
      rate_type: raw.rate_type.trim() || null,
      value: Number(raw.value),
      effective_date: raw.effective_date,
      is_non_refundable: raw.is_non_refundable,
      active: raw.active,
    };

    this.saving.set(true);
    const request = this.editingId()
      ? this.chargesService.update(this.editingId()!, payload)
      : this.chargesService.create(payload);

    request.subscribe({
      next: () => {
        this.toast.show(this.editingId() ? 'Charge updated' : 'Charge saved', '', 'success');
        this.saving.set(false);
        this.startCreate();
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.formError.set('Unable to save charge right now.');
        this.toast.show('Failed to save charge', '', 'danger');
      },
    });
  }

  protected remove(charge: Charge): void {
    if (!confirm(`Delete charge "${charge.charge_name}"?`)) return;
    this.chargesService.delete(charge.id).subscribe({
      next: () => {
        this.toast.show('Charge deleted', '', 'success');
        if (this.editingId() === charge.id) this.startCreate();
        this.load();
      },
      error: () => this.toast.show('Failed to delete charge', '', 'danger'),
    });
  }

  protected formatDate(value: string): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
  }

  protected formatCurrency(value: number | string): string {
    const amount = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }

  private load(): void {
    this.loading.set(true);
    this.chargesService.list().subscribe({
      next: (charges) => {
        this.charges.set(charges);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toast.show('Failed to load charges', '', 'danger');
      },
    });
  }

  private resetForm(): void {
    this.form.setValue({
      charge_type: '',
      charge_name: '',
      rate_type: '',
      value: 0,
      effective_date: this.today(),
      is_non_refundable: false,
      active: true,
    });
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private toInputDate(value: string | null | undefined): string {
    if (!value) return this.today();
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? this.today() : date.toISOString().slice(0, 10);
  }

  private normalizeChargeType(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }
}
